import { Checkbox, Chip } from '@heroui/react';
import { formatOversizeFactor, type OversizeResult } from '@estimator/shared';

interface OversizeControlProps {
  enabled: boolean;
  onChange: (v: boolean) => void;
  /** resolveOversize on the editor's live config. */
  result: OversizeResult;
  /** What the factor adds to the ex-fabric total. Omitted where the editor's
   *  total is not ex-fabric (the base's includes fabric), so the chip names
   *  only the factor. */
  extraCost?: number | null;
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/**
 * The per-template "Apply oversized charge" switch, and the factor the Library
 * applies at the dimensions currently typed. The editors render this in
 * library context only: a project line item is never charged oversize.
 */
export function OversizeControl({ enabled, onChange, result, extraCost }: OversizeControlProps) {
  const factor = formatOversizeFactor(result.factor);
  const chip = result.unreadable
    ? 'Oversize rules unreadable'
    : result.factor > 1
      ? extraCost != null
        ? `Oversized x${factor} (+${fmt(extraCost)})`
        : `Oversized x${factor} at these dimensions`
      : null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3">
        <Checkbox size="sm" isSelected={enabled} onValueChange={onChange}>
          <span className="text-xs">Apply oversized charge</span>
        </Checkbox>
        {chip && (
          <Chip size="sm" variant="flat" color={result.unreadable ? 'danger' : 'warning'}>
            {chip}
          </Chip>
        )}
      </div>
      {/* The Library and Cria carry these in `note`; without them here a
          non-finite dimension would drop its axis's charge silently. */}
      {result.warnings.map((w) => (
        <span key={w} className="text-xs" style={{ color: 'var(--orange)' }}>{w}</span>
      ))}
    </div>
  );
}
